import type { PracticeTestQuestion, QuestionAnswer } from '../services/practiceTestService';

interface Props {
  questions: PracticeTestQuestion[];
  answersMap: Map<number, QuestionAnswer>;
  markedForReview: Set<number>;
  currentIndex: number;
  onJumpTo: (index: number) => void;
  onClose?: () => void;
}

export default function QuestionPalette({ questions, answersMap, markedForReview, currentIndex, onJumpTo, onClose }: Props) {
  // Group questions by section
  const sections: { label: string; indices: number[] }[] = [];
  let currentSection = '';
  for (let i = 0; i < questions.length; i++) {
    const label = questions[i].section_label || '';
    if (label !== currentSection) {
      sections.push({ label, indices: [] });
      currentSection = label;
    }
    sections[sections.length - 1].indices.push(i);
  }

  function getStatus(idx: number): 'current' | 'answered' | 'review' | 'unanswered' {
    if (idx === currentIndex) return 'current';
    const qId = questions[idx].question_id;
    if (markedForReview.has(qId)) return 'review';
    if (answersMap.has(qId)) return 'answered';
    return 'unanswered';
  }

  const statusColors = {
    current: 'bg-coral-warm text-white ring-2 ring-coral-warm',
    answered: 'bg-green-500 text-white',
    review: 'bg-amber-400 text-white',
    unanswered: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
  };

  const answered = questions.filter(q => answersMap.has(q.question_id)).length;
  const reviewed = markedForReview.size;
  const unanswered = questions.length - answered;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-ocean-deep">Question Palette</h3>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-[10px]">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /> Answered ({answered})</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> Review ({reviewed})</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border" /> Not Answered ({unanswered})</span>
      </div>

      {/* Grid with section dividers */}
      {sections.map((section, sIdx) => (
        <div key={sIdx} className="mb-3">
          {section.label && (
            <p className="text-[10px] font-semibold text-coral-warm mb-1.5">{section.label}</p>
          )}
          <div className="grid grid-cols-8 gap-1.5">
            {section.indices.map((idx) => {
              const status = getStatus(idx);
              return (
                <button
                  key={idx}
                  onClick={() => onJumpTo(idx)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${statusColors[status]}`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
